import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import WappIcono from "../../../components/wappIcono";
import "./marcarFoto.css";

/**
 * Fotos antes de mandarlas (Esteban, 2026-10-26: "envié imagen con mensaje en WappTeam y se tardó un mundo en irse" y
 * "que puedan marcarla como en WhatsApp para cuando ocupen circular algo").
 *
 * 1. comprimirFoto: como WhatsApp, la foto se achica a 2048 px por lado y sale en JPEG desde el navegador. Una foto de
 *    celular de 4–8 MB queda en ~300–600 KB: lo que tardaba era subir la foto cruda (hasta 20 s en la oficina), no el
 *    servidor. Lo que el navegador no sabe abrir (HEIC en Chrome) se manda tal cual y lo convierte el servidor.
 * 2. MarcarFoto: lápiz, flecha, círculo, cuadro y texto encima de la foto, con colores, grosor y deshacer. Sale un JPEG
 *    nuevo con las marcas "quemadas", listo para mandar.
 * 3. Varias fotos (Esteban, 2026-10-27: "no fue fácil pasar a la siguiente foto en el celular, debería ser igual a WhatsApp"):
 *    todas van dentro del editor con su tira de miniaturas; sin herramienta elegida se desliza el dedo para cambiar de
 *    foto, cada una guarda sus marcas, y se puede escribir el comentario y enviar desde ahí.
 * 4. Recortar y girar (Esteban, 2026-11-04: "poder crop en el editor de fotos, igual de bueno que en WhatsApp, para cel y
 *    para PC"): recuadro con esquinas y orillas que se arrastran, cuadrícula, proporciones fijas, girar 90° y restablecer.
 *    El recorte no destruye nada (se puede volver a abrir y agrandar) y las marcas se recortan y giran con la foto. Al
 *    final se recorta de la foto ORIGINAL, no de la achicada, para que un recorte chico no salga borroso.
 */

/** Lado máximo de la foto que se manda (como WhatsApp). */
export const LADO_MAX = 2048;
/** Por debajo de esto no vale la pena recomprimir. */
const CHICA = 300 * 1024;

const nombreJpg = (n: string) => (n || "foto").replace(/\.[a-z0-9]+$/i, "") + ".jpg";
const aBlob = (c: HTMLCanvasElement, calidad: number): Promise<Blob | null> => new Promise((r) => c.toBlob((b) => r(b), "image/jpeg", calidad));

/** Carga la imagen en el navegador; null si no la sabe abrir (HEIC en Chrome/Windows, archivo dañado). */
const cargarImagen = (file: Blob): Promise<HTMLImageElement | null> => new Promise((resolve) => {
	const url = URL.createObjectURL(file);
	const img = new Image();
	img.onload = () => { URL.revokeObjectURL(url); resolve(img.naturalWidth > 0 ? img : null); };
	img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
	img.src = url;
});

/** GIF y WEBP se mandan tal cual (pueden ser animados; el servidor también los respeta). */
const esAnimable = (f: File) => /^image\/(gif|webp)$/i.test(f.type) || /\.(gif|webp)$/i.test(f.name || "");

/** La foto dibujada a tamaño de envío (≤ 2048 px por lado, fondo blanco bajo lo transparente, derecha según el EXIF). */
const lienzoBase = (img: HTMLImageElement): HTMLCanvasElement | null => {
	const esc = Math.min(1, LADO_MAX / Math.max(img.naturalWidth, img.naturalHeight));
	const c = document.createElement("canvas");
	c.width = Math.max(1, Math.round(img.naturalWidth * esc));
	c.height = Math.max(1, Math.round(img.naturalHeight * esc));
	const ctx = c.getContext("2d");
	if (!ctx) return null;
	ctx.imageSmoothingEnabled = true;
	ctx.imageSmoothingQuality = "high";
	ctx.fillStyle = "#fff";
	ctx.fillRect(0, 0, c.width, c.height);
	ctx.drawImage(img, 0, 0, c.width, c.height);
	return c;
};

/**
 * Achica y recomprime una foto antes de subirla. Devuelve el mismo archivo cuando ya es chica, es GIF/WEBP, el navegador
 * no la sabe abrir o la versión nueva no quedó más liviana.
 */
export const comprimirFoto = async (file: File): Promise<File> => {
	if (file.size < CHICA || esAnimable(file)) return file;
	const img = await cargarImagen(file);
	if (!img) return file;
	const c = lienzoBase(img);
	if (!c) return file;
	const b = await aBlob(c, 0.85);
	if (!b || b.size >= file.size) return file;
	return new File([b], nombreJpg(file.name), { type: "image/jpeg", lastModified: Date.now() });
};


// ------------------------------------------------------------------ editor

type Herramienta = "lapiz" | "flecha" | "circulo" | "cuadro" | "texto";
interface Punto { x: number; y: number }
/** pts siempre en coordenadas de la foto SIN girar ni recortar (así las marcas viajan con la foto). */
interface Trazo { t: Herramienta; color: string; grosor: number; pts: Punto[]; texto?: string; giro?: Giro }

/** Cuartos de vuelta a la derecha (3 = un cuarto a la izquierda). */
type Giro = 0 | 1 | 2 | 3;
interface Rect { x: number; y: number; w: number; h: number }
/** rect en coordenadas de la foto ya girada; null = la foto completa. */
interface Ajuste { giro: Giro; rect: Rect | null }
const SIN_AJUSTE: Ajuste = { giro: 0, rect: null };

const HERRAMIENTAS: { id: Herramienta; nombre: string; icono: React.ReactNode }[] = [
	{ id: "lapiz", nombre: "Lápiz", icono: <svg viewBox="0 0 24 24"><path d="M4 20c4-1 5-3 6-6s3-6 6-7c2-1 4 1 3 3-1 3-5 4-8 5s-5 3-7 5" /></svg> },
	{ id: "flecha", nombre: "Flecha", icono: <svg viewBox="0 0 24 24"><path d="M5 19L19 5M10 5h9v9" /></svg> },
	{ id: "circulo", nombre: "Círculo", icono: <svg viewBox="0 0 24 24"><ellipse cx="12" cy="12" rx="8.5" ry="7" /></svg> },
	{ id: "cuadro", nombre: "Cuadro", icono: <svg viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="14" rx="2" /></svg> },
	{ id: "texto", nombre: "Texto", icono: <svg viewBox="0 0 24 24"><path d="M5 6V4h14v2M12 4v16M9 20h6" /></svg> },
];
const ICONO_RECORTAR = <svg viewBox="0 0 24 24"><path d="M6 2v14a2 2 0 0 0 2 2h14M2 6h14a2 2 0 0 1 2 2v14" /></svg>;
const ICONO_GIRAR = <svg viewBox="0 0 24 24"><path d="M3 4v5h5" /><path d="M3.5 9A9 9 0 1 1 5 17" /></svg>;
const COLORES = ["#E53935", "#FDD835", "#43A047", "#1E88E5", "#FFFFFF", "#111111"];
const GROSORES: { id: number; nombre: string }[] = [{ id: 0.6, nombre: "Fino" }, { id: 1, nombre: "Medio" }, { id: 1.8, nombre: "Grueso" }];

/** Proporciones del recorte, como WhatsApp. */
const ASPECTOS: { id: string; nombre: string; r?: number }[] = [
	{ id: "libre", nombre: "Libre" }, { id: "original", nombre: "Original" }, { id: "1:1", nombre: "Cuadrado", r: 1 },
	{ id: "4:3", nombre: "4:3", r: 4 / 3 }, { id: "3:4", nombre: "3:4", r: 3 / 4 }, { id: "16:9", nombre: "16:9", r: 16 / 9 }, { id: "9:16", nombre: "9:16", r: 9 / 16 },
];
/** Al girar, la proporción también gira. */
const ASPECTO_GIRADO: Record<string, string> = { "4:3": "3:4", "3:4": "4:3", "16:9": "9:16", "9:16": "16:9" };

const dimsGiradas = (W: number, H: number, g: Giro) => (g % 2 ? { w: H, h: W } : { w: W, h: H });
const rectDe = (aj: Ajuste, W: number, H: number): Rect => aj.rect || { x: 0, y: 0, ...dimsGiradas(W, H, aj.giro) };
const ajusteVacio = (aj?: Ajuste) => !aj || (aj.giro === 0 && !aj.rect);
/** Gira el contexto: después se dibuja la foto en sus coordenadas originales (0,0,W,H). */
const aplicarGiro = (ctx: CanvasRenderingContext2D, g: Giro, W: number, H: number) => {
	if (g === 1) ctx.transform(0, 1, -1, 0, H, 0);
	else if (g === 2) ctx.transform(-1, 0, 0, -1, W, H);
	else if (g === 3) ctx.transform(0, -1, 1, 0, 0, W);
};
/** De la foto girada a la original (lo inverso de aplicarGiro). */
const aOriginal = (u: number, v: number, g: Giro, W: number, H: number): Punto =>
	g === 1 ? { x: v, y: H - u } : g === 2 ? { x: W - u, y: H - v } : g === 3 ? { x: W - v, y: u } : { x: u, y: v };
/** Deja el contexto listo para dibujar en coordenadas originales: escala s, recorte y giro. */
const prepararVista = (ctx: CanvasRenderingContext2D, aj: Ajuste, W: number, H: number, s: number) => {
	const r = rectDe(aj, W, H);
	ctx.setTransform(s, 0, 0, s, 0, 0);
	ctx.translate(-r.x, -r.y);
	aplicarGiro(ctx, aj.giro, W, H);
};
const limitar = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

/** Pinta un trazo en coordenadas de la foto. */
const pintar = (ctx: CanvasRenderingContext2D, z: Trazo, base: number) => {
	const g = Math.max(2, base * z.grosor);
	ctx.save();
	ctx.strokeStyle = z.color;
	ctx.fillStyle = z.color;
	ctx.lineWidth = g;
	ctx.lineCap = "round";
	ctx.lineJoin = "round";
	const a = z.pts[0];
	const b = z.pts[z.pts.length - 1];
	if (!a) { ctx.restore(); return; }
	if (z.t === "lapiz") {
		ctx.beginPath();
		ctx.moveTo(a.x, a.y);
		if (z.pts.length === 1) ctx.lineTo(a.x + 0.1, a.y);
		for (let i = 1; i < z.pts.length; i++) ctx.lineTo(z.pts[i].x, z.pts[i].y);
		ctx.stroke();
	} else if (z.t === "flecha") {
		const ang = Math.atan2(b.y - a.y, b.x - a.x);
		const cabeza = Math.max(g * 3.5, 14);
		ctx.beginPath();
		ctx.moveTo(a.x, a.y);
		ctx.lineTo(b.x - Math.cos(ang) * cabeza * 0.6, b.y - Math.sin(ang) * cabeza * 0.6);
		ctx.stroke();
		ctx.beginPath();
		ctx.moveTo(b.x, b.y);
		ctx.lineTo(b.x - cabeza * Math.cos(ang - Math.PI / 6), b.y - cabeza * Math.sin(ang - Math.PI / 6));
		ctx.lineTo(b.x - cabeza * Math.cos(ang + Math.PI / 6), b.y - cabeza * Math.sin(ang + Math.PI / 6));
		ctx.closePath();
		ctx.fill();
	} else if (z.t === "circulo") {
		const rx = Math.abs(b.x - a.x) / 2;
		const ry = Math.abs(b.y - a.y) / 2;
		if (rx > 0.5 && ry > 0.5) {
			ctx.beginPath();
			ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, rx, ry, 0, 0, Math.PI * 2);
			ctx.stroke();
		}
	} else if (z.t === "cuadro") {
		ctx.strokeRect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
	} else if (z.t === "texto" && z.texto) {
		const tam = Math.round(base * 7 * z.grosor);
		// El texto queda derecho en pantalla con el giro que tenía la foto cuando se escribió; si luego se gira, gira con ella.
		ctx.translate(a.x, a.y);
		if (z.giro) ctx.rotate(-z.giro * Math.PI / 2);
		ctx.font = "700 " + tam + "px 'Hanken Grotesk', Arial, sans-serif";
		ctx.textBaseline = "top";
		ctx.lineWidth = Math.max(2, tam / 7);
		ctx.strokeStyle = z.color === "#FFFFFF" || z.color === "#FDD835" ? "rgba(0,0,0,.75)" : "rgba(255,255,255,.85)";
		z.texto.split("\n").forEach((linea, i) => {
			ctx.strokeText(linea, 0, i * tam * 1.2);
			ctx.fillText(linea, 0, i * tam * 1.2);
		});
	}
	ctx.restore();
};

export interface FotoParaMarcar { id: string; file: File; url: string }
export interface FotoMarcada { id: string; file: File }

const SIN_TRAZOS: Trazo[] = [];
/** Grosor base proporcional a la foto (en una de 2048 px, "medio" ≈ 8 px). */
const unidadDe = (b: HTMLCanvasElement | null | undefined) => (b ? Math.max(3, Math.min(b.width, b.height) / 250) : 4);

/**
 * Dibuja la foto ya recortada y girada, con sus marcas, a escala s (coordenadas de la foto achicada × s). Si la escala
 * pide más detalle que la foto achicada, se dibuja desde la original.
 */
const componer = (ctx: CanvasRenderingContext2D, base: HTMLCanvasElement, img: HTMLImageElement | null | undefined, aj: Ajuste, s: number, trazos: Trazo[]) => {
	const W = base.width; const H = base.height;
	ctx.setTransform(1, 0, 0, 1, 0, 0);
	ctx.fillStyle = "#fff";
	ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
	ctx.imageSmoothingEnabled = true;
	ctx.imageSmoothingQuality = "high";
	prepararVista(ctx, aj, W, H, s);
	ctx.drawImage(img && s > 1.01 && img.naturalWidth > W ? img : base, 0, 0, W, H);
	const u = unidadDe(base);
	trazos.forEach((z) => pintar(ctx, z, u));
	ctx.setTransform(1, 0, 0, 1, 0, 0);
};

/**
 * Nuevo tamaño del recuadro de recorte al arrastrar. tipo: "mover" o las orillas que se mueven (n, s, e, w, ne, nw…).
 * Todo en coordenadas de la foto girada (RW × RH); min = lado mínimo.
 */
const arrastrarRecorte = (tipo: string, r0: Rect, dx: number, dy: number, RW: number, RH: number, ratio: number | null, min: number): Rect => {
	if (tipo === "mover") return { ...r0, x: limitar(r0.x + dx, 0, RW - r0.w), y: limitar(r0.y + dy, 0, RH - r0.h) };
	const L = tipo.includes("w"); const R = tipo.includes("e"); const T = tipo.includes("n"); const B = tipo.includes("s");
	let x1 = r0.x; let y1 = r0.y; let x2 = r0.x + r0.w; let y2 = r0.y + r0.h;
	if (ratio && (L || R) && (T || B)) {
		// Esquina con proporción fija: la esquina contraria se queda quieta.
		const ax = L ? x2 : x1; const ay = T ? y2 : y1;
		const px = (L ? x1 : x2) + dx; const py = (T ? y1 : y2) + dy;
		let w = Math.max(L ? ax - px : px - ax, (T ? ay - py : py - ay) * ratio);
		const minW = Math.max(min, min * ratio);
		w = Math.min(Math.max(w, minW), L ? ax : RW - ax, (T ? ay : RH - ay) * ratio);
		const h = w / ratio;
		return { x: L ? ax - w : ax, y: T ? ay - h : ay, w, h };
	}
	if (ratio) {
		// Orilla con proporción fija: crece hacia ese lado y se centra en el otro eje.
		if (L || R) {
			let w = (R ? x2 + dx : x2) - (L ? x1 + dx : x1);
			const cy = (y1 + y2) / 2;
			w = Math.min(Math.max(w, Math.max(min, min * ratio)), L ? x2 : RW - x1, RH * ratio);
			const h = w / ratio;
			return { x: L ? x2 - w : x1, y: limitar(cy - h / 2, 0, RH - h), w, h };
		}
		let h = (B ? y2 + dy : y2) - (T ? y1 + dy : y1);
		const cx = (x1 + x2) / 2;
		h = Math.min(Math.max(h, Math.max(min, min / ratio)), T ? y2 : RH - y1, RW / ratio);
		const w = h * ratio;
		return { x: limitar(cx - w / 2, 0, RW - w), y: T ? y2 - h : y1, w, h };
	}
	if (L) x1 = limitar(x1 + dx, 0, x2 - min);
	if (R) x2 = limitar(x2 + dx, x1 + min, RW);
	if (T) y1 = limitar(y1 + dy, 0, y2 - min);
	if (B) y2 = limitar(y2 + dy, y1 + min, RH);
	return { x: x1, y: y1, w: x2 - x1, h: y2 - y1 };
};

/** Margen alrededor de la foto al recortar, para que las esquinas no queden pegadas a la orilla de la pantalla. */
const MARGEN_RECORTE = 30;
/** Hasta cuánto se puede acercar la foto al recortar (8 = ocho veces más grande que cabiendo completa). */
const ZOOM_MAX = 8;
/** Vista al recortar: zoom y qué punto de la foto (girada) queda al centro del marco. */
interface Vista { z: number; cx: number; cy: number }
const VISTA_INICIAL: Vista = { z: 1, cx: 0, cy: 0 };
const ASAS = ["n", "s", "e", "w", "nw", "ne", "sw", "se"];

/**
 * Editor de fotos, una o varias: recortar, girar y marcar. onListo recibe solo las fotos que se tocaron (JPEG nuevo);
 * onEnviar hace lo mismo y además pide mandar. Funciona con mouse y con el dedo.
 */
export const MarcarFoto: React.FC<{
	fotos: FotoParaMarcar[]; inicial?: string;
	onListo: (marcadas: FotoMarcada[]) => void; onCerrar: () => void;
	/** Comentario compartido con la bandeja de abajo (se escribe en cualquiera de los dos). */
	comentario?: string; onComentario?: (t: string) => void;
	/** Sin esto no sale el botón de enviar dentro del editor. */
	onEnviar?: (marcadas: FotoMarcada[]) => void; textoEnviar?: string;
}> = ({ fotos, inicial, onListo, onCerrar, comentario, onComentario, onEnviar, textoEnviar }) => {
	const [i, setI] = useState(() => Math.max(0, fotos.findIndex((f) => f.id === inicial)));
	const idx = Math.min(i, Math.max(0, fotos.length - 1));
	const foto = fotos[idx];
	const varias = fotos.length > 1;
	// Fotos ya abiertas (null = el navegador no la sabe abrir). Se guardan para no recargarlas al ir y venir.
	const bases = useRef(new Map<string, HTMLCanvasElement | null>());
	const originales = useRef(new Map<string, HTMLImageElement>());
	const cargando = useRef(new Set<string>());
	const montado = useRef(true);
	const [, setCargadas] = useState(0);
	// Como WhatsApp: sin herramienta elegida el dedo cambia de foto; al elegir una, dibuja.
	const [herr, setHerr] = useState<Herramienta | null>(null);
	const [color, setColor] = useState(COLORES[0]);
	const [grosor, setGrosor] = useState(1);
	const [marcas, setMarcas] = useState<Record<string, Trazo[]>>({});
	const [ajustes, setAjustes] = useState<Record<string, Ajuste>>({});
	const [minis, setMinis] = useState<Record<string, string>>({});
	const [enCurso, setEnCurso] = useState<Trazo | null>(null);
	/** Dónde se está escribiendo, en coordenadas de la vista (foto ya recortada). */
	const [textoEn, setTextoEn] = useState<Punto | null>(null);
	const [texto, setTexto] = useState("");
	const [guardando, setGuardando] = useState(false);
	const [dx, setDx] = useState(0);
	// Recortar: borrador del ajuste mientras se mueve el recuadro (se aplica con "Aplicar").
	const [recorte, setRecorte] = useState<{ giro: Giro; rect: Rect } | null>(null);
	const [aspecto, setAspecto] = useState("libre");
	const [moviendo, setMoviendo] = useState(false);
	const [area, setArea] = useState({ w: 0, h: 0, mw: 0, mh: 0 });
	// Acercar al recortar (Esteban, 2026-11-04): pellizcar con dos dedos, rueda del mouse o pellizco del trackpad.
	const [vista, setVista] = useState<Vista>(VISTA_INICIAL);
	const capaRef = useRef<HTMLDivElement>(null);
	const dedos = useRef(new Map<number, { x: number; y: number }>());
	const pellizco = useRef<{ d: number; mx: number; my: number; v: Vista } | null>(null);
	const anim = useRef(0);
	const recRef = useRef<{ giro: Giro; rect: Rect } | null>(null);
	const vistaRef = useRef<Vista>(VISTA_INICIAL);
	const zoomRef = useRef<(x: number, y: number, f: number) => void>(() => undefined);
	const lienzo = useRef<HTMLCanvasElement>(null);
	const marco = useRef<HTMLDivElement>(null);
	const cajaTexto = useRef<HTMLTextAreaElement>(null);
	const tira = useRef<HTMLDivElement>(null);
	const pintando = useRef(false);
	const abiertoEn = useRef(0);
	const desliz = useRef<{ x: number; y: number } | null>(null);
	const arrastre = useRef<{ tipo: string; x: number; y: number; r0: Rect; v: Vista } | null>(null);

	const id = foto?.id || "";
	const base = bases.current.get(id) || null;
	const noAbre = bases.current.has(id) && !base;
	const trazos = marcas[id] || SIN_TRAZOS;
	const unidad = unidadDe(base);
	const W = base?.width || 1; const H = base?.height || 1;
	const recortando = !!recorte;
	const ajuste = ajustes[id] || SIN_AJUSTE;
	// Lo que se ve: al recortar, la foto girada completa; si no, la foto ya recortada.
	const vistaAj: Ajuste = recorte ? { giro: recorte.giro, rect: null } : ajuste;
	const vr = rectDe(vistaAj, W, H);
	const setTrazos = (fn: (t: Trazo[]) => Trazo[]) => setMarcas((m) => ({ ...m, [id]: fn(m[id] || SIN_TRAZOS) }));
	const hayMarcas = Object.values(marcas).some((t) => t.length) || Object.values(ajustes).some((a) => !ajusteVacio(a)) || !!(textoEn && texto.trim());

	// Tamaño en pantalla: la vista cabe en el marco (también agranda un recorte chico).
	const margen = recortando ? MARGEN_RECORTE : 0;
	const k = area.w > 0 ? Math.min((area.w - 2 * margen) / vr.w, (area.h - 2 * margen) / vr.h, 4) : 0;
	const cssW = Math.max(1, Math.round(vr.w * k)); const cssH = Math.max(1, Math.round(vr.h * k));
	const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
	const s = limitar(k * dpr, 0.25, 4);
	const intW = Math.max(1, Math.round(vr.w * s)); const intH = Math.max(1, Math.round(vr.h * s));
	// Al recortar el lienzo ocupa todo el marco y la foto se dibuja con zoom: K = pantalla por pixel de la foto girada.
	const RWr = recorte ? dimsGiradas(W, H, recorte.giro).w : vr.w;
	const RHr = recorte ? dimsGiradas(W, H, recorte.giro).h : vr.h;
	const k0 = recortando && area.mw > 0 ? Math.min((area.mw - 2 * MARGEN_RECORTE) / RWr, (area.mh - 2 * MARGEN_RECORTE) / RHr, 4) : 0;
	/**
	 * La vista dentro de lo permitido: en el eje donde la foto cabe completa va centrada; si no, el centro de la pantalla
	 * siempre cae sobre la foto (así un recuadro en la orilla de la foto se puede centrar al acercar).
	 */
	const fijar = (v: Vista): Vista => {
		const z = limitar(v.z, 1, ZOOM_MAX); const K = k0 * z;
		if (!K) return { z, cx: RWr / 2, cy: RHr / 2 };
		const cabeX = RWr * K <= area.mw - 2 * MARGEN_RECORTE + 0.5; const cabeY = RHr * K <= area.mh - 2 * MARGEN_RECORTE + 0.5;
		return { z, cx: cabeX ? RWr / 2 : limitar(v.cx, 0, RWr), cy: cabeY ? RHr / 2 : limitar(v.cy, 0, RHr) };
	};
	/** Al acercar, el recuadro no se sale de la pantalla: se queda con la parte que se ve (como WhatsApp). */
	const recuadroVisible = (r: Rect, v: Vista): Rect => {
		const K1 = k0 * v.z; if (!K1) return r;
		const x0 = area.mw / 2 - v.cx * K1; const y0 = area.mh / 2 - v.cy * K1;
		const L = Math.max(r.x, (MARGEN_RECORTE - x0) / K1); const R = Math.min(r.x + r.w, (area.mw - MARGEN_RECORTE - x0) / K1);
		const T = Math.max(r.y, (MARGEN_RECORTE - y0) / K1); const B = Math.min(r.y + r.h, (area.mh - MARGEN_RECORTE - y0) / K1);
		if (R - L < 4 || B - T < 4) return r;
		if (L <= r.x && T <= r.y && R >= r.x + r.w && B >= r.y + r.h) return r;
		const ratio = ratioDe(aspecto, RWr, RHr);
		if (!ratio) return { x: L, y: T, w: R - L, h: B - T };
		const w = Math.min(R - L, (B - T) * ratio); const h = w / ratio;
		return { x: (L + R) / 2 - w / 2, y: (T + B) / 2 - h / 2, w, h };
	};
	const vz = fijar(vista);
	const K = k0 * vz.z;
	const ox = area.mw / 2 - vz.cx * K; const oy = area.mh / 2 - vz.cy * K;
	const dc = Math.min(dpr, 2);
	recRef.current = recorte; vistaRef.current = vz;

	useEffect(() => () => { montado.current = false; }, []);
	// Abre la foto actual y deja lista la siguiente.
	useEffect(() => {
		const cargar = (f?: FotoParaMarcar): Promise<void> => {
			if (!f || bases.current.has(f.id) || cargando.current.has(f.id)) return Promise.resolve();
			cargando.current.add(f.id);
			return cargarImagen(f.file).then((img) => {
				bases.current.set(f.id, img ? lienzoBase(img) : null);
				if (img) originales.current.set(f.id, img);
				cargando.current.delete(f.id);
				if (montado.current) setCargadas((n) => n + 1);
			});
		};
		cargar(foto).then(() => cargar(fotos[idx + 1]));
	}, [id]);
	// La miniatura activa siempre a la vista.
	useEffect(() => {
		(tira.current?.children[idx] as HTMLElement | undefined)?.scrollIntoView?.({ block: "nearest", inline: "center", behavior: "smooth" });
	}, [idx]);
	// Medir el espacio disponible (cambia al girar el celular, al abrir el teclado o al entrar a recortar).
	useLayoutEffect(() => {
		const m = marco.current;
		if (!m) return undefined;
		const medir = () => {
			const cs = window.getComputedStyle(m);
			const pw = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
			const ph = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
			const n = { w: Math.max(0, m.clientWidth - pw), h: Math.max(0, m.clientHeight - ph), mw: m.clientWidth, mh: m.clientHeight };
			setArea((a) => (a.w === n.w && a.h === n.h && a.mw === n.mw && a.mh === n.mh ? a : n));
		};
		medir();
		const RO = (window as any).ResizeObserver;
		if (RO) { const ro = new RO(medir); ro.observe(m); return () => ro.disconnect(); }
		window.addEventListener("resize", medir);
		return () => window.removeEventListener("resize", medir);
	}, []);

	// La foto sola (sin marcas) a la resolución de pantalla; se recalcula solo al recortar, girar o cambiar el tamaño.
	const fondo = useMemo(() => {
		if (!base || recortando) return null;
		const c = document.createElement("canvas");
		c.width = intW; c.height = intH;
		const ctx = c.getContext("2d");
		if (!ctx) return null;
		componer(ctx, base, originales.current.get(id), vistaAj, s, SIN_TRAZOS);
		return c;
	}, [base, id, vistaAj.giro, vr.x, vr.y, vr.w, vr.h, intW, intH, s, recortando]);

	// Repinta: la foto + los trazos + el que se está dibujando.
	useEffect(() => {
		const c = lienzo.current;
		if (!c || !base) return;
		if (recorte) {
			// Recortando: todo el marco, la foto girada con zoom (desde la original si el zoom pide más detalle).
			const cw = Math.max(1, Math.round(area.mw * dc)); const ch = Math.max(1, Math.round(area.mh * dc));
			if (c.width !== cw || c.height !== ch) { c.width = cw; c.height = ch; }
			const ctx = c.getContext("2d");
			if (!ctx || !K) return;
			ctx.setTransform(1, 0, 0, 1, 0, 0);
			ctx.clearRect(0, 0, cw, ch);
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = "high";
			ctx.setTransform(dc * K, 0, 0, dc * K, dc * ox, dc * oy);
			aplicarGiro(ctx, recorte.giro, W, H);
			ctx.fillStyle = "#fff";
			ctx.fillRect(0, 0, W, H);
			const img = originales.current.get(id);
			ctx.drawImage(img && K * dc > 1.05 && img.naturalWidth > W ? img : base, 0, 0, W, H);
			trazos.forEach((z) => pintar(ctx, z, unidad));
			ctx.setTransform(1, 0, 0, 1, 0, 0);
			return;
		}
		if (!fondo) return;
		if (c.width !== intW || c.height !== intH) { c.width = intW; c.height = intH; }
		const ctx = c.getContext("2d");
		if (!ctx) return;
		ctx.setTransform(1, 0, 0, 1, 0, 0);
		ctx.drawImage(fondo, 0, 0);
		prepararVista(ctx, vistaAj, W, H, s);
		trazos.forEach((z) => pintar(ctx, z, unidad));
		if (enCurso) pintar(ctx, enCurso, unidad);
		ctx.setTransform(1, 0, 0, 1, 0, 0);
	}, [fondo, trazos, enCurso, unidad, recorte?.giro, K, ox, oy, area.mw, area.mh, dc]);

	useEffect(() => { if (textoEn) setTimeout(() => cajaTexto.current?.focus(), 0); }, [textoEn]);

	/** El texto que se está escribiendo, ya como trazo en coordenadas de la foto original. */
	const trazoTexto = (): Trazo | null => {
		if (!textoEn || !texto.trim()) return null;
		const p = aOriginal(textoEn.x + vr.x, textoEn.y + vr.y, vistaAj.giro, W, H);
		return { t: "texto", color, grosor, pts: [p], texto: texto.trim(), giro: vistaAj.giro };
	};
	const confirmarTexto = () => {
		const z = trazoTexto();
		if (z) setTrazos((t) => [...t, z]);
		setTextoEn(null); setTexto("");
	};
	const ir = (n: number) => {
		if (recortando || n < 0 || n >= fotos.length || n === idx) return;
		confirmarTexto(); pintando.current = false; setEnCurso(null); setI(n);
	};
	const elegirHerr = (h: Herramienta) => { confirmarTexto(); setHerr((a) => (a === h ? null : h)); };
	const cancelar = () => {
		if (guardando) return;
		if (hayMarcas && !window.confirm("¿Descartar los cambios de la foto?")) return;
		onCerrar();
	};

	// ---- recortar
	const ratioDe = (a: string, rw: number, rh: number): number | null => (a === "original" ? rw / rh : ASPECTOS.find((x) => x.id === a)?.r || null);
	const entrarRecorte = () => {
		if (!base || guardando) return;
		confirmarTexto(); pintando.current = false; setEnCurso(null); setHerr(null);
		setRecorte({ giro: ajuste.giro, rect: rectDe(ajuste, W, H) });
		setAspecto("libre"); setVista(VISTA_INICIAL);
	};
	const salirRecorte = () => {
		cancelAnimationFrame(anim.current);
		arrastre.current = null; pellizco.current = null; dedos.current.clear();
		setMoviendo(false); setRecorte(null);
	};
	/** Miniatura de la tira con el recorte puesto (las marcas se ven con el globito). */
	const hacerMini = (fid: string, aj: Ajuste) => {
		const b = bases.current.get(fid);
		if (!b) return;
		if (ajusteVacio(aj)) { setMinis((m) => { const n = { ...m }; delete n[fid]; return n; }); return; }
		const r = rectDe(aj, b.width, b.height);
		const e = 120 / Math.max(r.w, r.h);
		const c = document.createElement("canvas");
		c.width = Math.max(1, Math.round(r.w * e)); c.height = Math.max(1, Math.round(r.h * e));
		const ctx = c.getContext("2d");
		if (!ctx) return;
		componer(ctx, b, null, aj, e, SIN_TRAZOS);
		const url = c.toDataURL("image/jpeg", 0.75);
		setMinis((m) => ({ ...m, [fid]: url }));
	};
	const aplicarRecorte = () => {
		if (!recorte) return;
		const { w: rw, h: rh } = dimsGiradas(W, H, recorte.giro);
		const r = recorte.rect;
		const completo = r.x < 1 && r.y < 1 && r.w > rw - 1 && r.h > rh - 1;
		const aj: Ajuste = { giro: recorte.giro, rect: completo ? null : { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.w), h: Math.round(r.h) } };
		setAjustes((m) => { const n = { ...m }; if (ajusteVacio(aj)) delete n[id]; else n[id] = aj; return n; });
		hacerMini(id, aj);
		salirRecorte();
	};
	const girar = () => {
		setRecorte((rc) => {
			if (!rc) return rc;
			// Un cuarto de vuelta a la izquierda (como WhatsApp); el recuadro gira con la foto.
			const RW = dimsGiradas(W, H, rc.giro).w;
			const r = rc.rect;
			return { giro: ((rc.giro + 3) % 4) as Giro, rect: { x: r.y, y: RW - r.x - r.w, w: r.h, h: r.w } };
		});
		setAspecto((a) => ASPECTO_GIRADO[a] || a);
		cancelAnimationFrame(anim.current); setVista(VISTA_INICIAL);
	};
	const restablecer = () => { cancelAnimationFrame(anim.current); setRecorte({ giro: 0, rect: { x: 0, y: 0, w: W, h: H } }); setAspecto("libre"); setVista(VISTA_INICIAL); };
	const elegirAspecto = (a: string) => {
		setAspecto(a);
		setRecorte((rc) => {
			if (!rc) return rc;
			const { w: RW, h: RH } = dimsGiradas(W, H, rc.giro);
			const ratio = ratioDe(a, RW, RH);
			if (!ratio) return rc;
			// El más grande que cabe con esa proporción, centrado donde estaba el recuadro.
			const w = Math.min(RW, RH * ratio); const h = w / ratio;
			const cx = rc.rect.x + rc.rect.w / 2; const cy = rc.rect.y + rc.rect.h / 2;
			return { ...rc, rect: { x: limitar(cx - w / 2, 0, RW - w), y: limitar(cy - h / 2, 0, RH - h), w, h } };
		});
		encuadrar();
	};
	/**
	 * Como WhatsApp: al soltar una esquina u orilla, la foto se acerca sola para que el recuadro llene la pantalla, y así se
	 * puede seguir afinando. Corre en el siguiente cuadro para leer el recuadro ya actualizado.
	 */
	const encuadrar = (siNoSeVe = false) => {
		cancelAnimationFrame(anim.current);
		anim.current = requestAnimationFrame(() => {
			const rc = recRef.current; const v0 = vistaRef.current;
			if (!rc || !k0) return;
			const r = rc.rect;
			if (siNoSeVe) {
				const K0 = k0 * v0.z; const x0 = area.mw / 2 - v0.cx * K0; const y0 = area.mh / 2 - v0.cy * K0;
				const fuera = x0 + r.x * K0 < 0 || y0 + r.y * K0 < 0 || x0 + (r.x + r.w) * K0 > area.mw || y0 + (r.y + r.h) * K0 > area.mh;
				if (!fuera) return;
			}
			const meta = fijar({ z: Math.min((area.mw - 2 * MARGEN_RECORTE) / r.w, (area.mh - 2 * MARGEN_RECORTE) / r.h) / k0, cx: r.x + r.w / 2, cy: r.y + r.h / 2 });
			const t0 = performance.now();
			const paso = (t: number) => {
				const f = Math.min(1, (t - t0) / 240); const e = 1 - Math.pow(1 - f, 3);
				setVista(fijar({ z: v0.z * Math.pow(meta.z / v0.z, e), cx: v0.cx + (meta.cx - v0.cx) * e, cy: v0.cy + (meta.cy - v0.cy) * e }));
				if (f < 1) anim.current = requestAnimationFrame(paso);
			};
			anim.current = requestAnimationFrame(paso);
		});
	};
	/** Acercar o alejar dejando quieto el punto (clientX, clientY) de la pantalla. */
	const zoomEn = (x: number, y: number, z1: number, desde: Vista = vz, en?: { x: number; y: number }) => {
		const m = marco.current; if (!m || !k0) return;
		const mr = m.getBoundingClientRect();
		const K0 = k0 * desde.z; const x0 = area.mw / 2 - desde.cx * K0; const y0 = area.mh / 2 - desde.cy * K0;
		const px = (x - mr.left - x0) / K0; const py = (y - mr.top - y0) / K0;   // punto de la foto bajo los dedos
		const z = limitar(z1, 1, ZOOM_MAX); const K1 = k0 * z;
		const dx2 = (en ? en.x : x) - mr.left; const dy2 = (en ? en.y : y) - mr.top;   // dónde debe quedar ese punto
		const nv = fijar({ z, cx: (area.mw / 2 - (dx2 - px * K1)) / K1, cy: (area.mh / 2 - (dy2 - py * K1)) / K1 });
		setVista(nv);
		setRecorte((rc) => (rc ? { ...rc, rect: recuadroVisible(rc.rect, nv) } : rc));
	};
	zoomRef.current = (x, y, f) => { cancelAnimationFrame(anim.current); zoomEn(x, y, vz.z * f); };
	const empezarPellizco = () => {
		const [a, b] = Array.from(dedos.current.values());
		arrastre.current = null; setMoviendo(false);
		pellizco.current = { d: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2, v: vz };
	};
	const recBajar = (e: React.PointerEvent) => {
		if (!recorte) return;
		if (e.pointerType === "mouse" && e.button !== 0) return;
		e.preventDefault();
		capaRef.current?.setPointerCapture?.(e.pointerId);
		cancelAnimationFrame(anim.current);
		dedos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		if (dedos.current.size >= 2) { empezarPellizco(); return; }
		// Esquina u orilla: cambia el tamaño · dentro del recuadro: lo mueve · fuera: mueve la foto (si está acercada).
		let tipo = (e.target as HTMLElement).getAttribute("data-asa") || "foto";
		// Acercada, arrastrar dentro del recuadro mueve la foto debajo de él (el recuadro se queda quieto en la pantalla).
		if (tipo === "mover" && vz.z > 1.01) tipo = "debajo";
		arrastre.current = { tipo, x: e.clientX, y: e.clientY, r0: recorte.rect, v: vz };
		setMoviendo(tipo !== "foto");
	};
	const recMover = (e: React.PointerEvent) => {
		if (!dedos.current.has(e.pointerId)) return;
		dedos.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
		const p = pellizco.current;
		if (p && dedos.current.size >= 2) {
			const [a, b] = Array.from(dedos.current.values());
			const d = Math.hypot(a.x - b.x, a.y - b.y);
			zoomEn(p.mx, p.my, p.v.z * d / p.d, p.v, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
			return;
		}
		const a = arrastre.current;
		if (!a || !K) return;
		e.preventDefault();
		if (a.tipo === "debajo") {
			const K0 = k0 * a.v.z;
			const nx = limitar(a.r0.x - (e.clientX - a.x) / K0, 0, RWr - a.r0.w);
			const ny = limitar(a.r0.y - (e.clientY - a.y) / K0, 0, RHr - a.r0.h);
			setRecorte((rc) => (rc ? { ...rc, rect: { ...a.r0, x: nx, y: ny } } : rc));
			setVista(fijar({ ...a.v, cx: a.v.cx + (nx - a.r0.x), cy: a.v.cy + (ny - a.r0.y) }));
			return;
		}
		if (a.tipo === "foto") {
			const K0 = k0 * a.v.z;
			setVista(fijar({ ...a.v, cx: a.v.cx - (e.clientX - a.x) / K0, cy: a.v.cy - (e.clientY - a.y) / K0 }));
			return;
		}
		const ratio = ratioDe(aspecto, RWr, RHr);
		const r = arrastrarRecorte(a.tipo, a.r0, (e.clientX - a.x) / K, (e.clientY - a.y) / K, RWr, RHr, ratio, Math.min(52 / K, RWr, RHr));
		setRecorte((rc) => (rc ? { ...rc, rect: r } : rc));
	};
	const recSoltar = (e: React.PointerEvent) => {
		dedos.current.delete(e.pointerId);
		if (pellizco.current) {
			if (dedos.current.size < 2) {
				pellizco.current = null;
				// El dedo que queda sigue moviendo la foto, sin brincos.
				const q = Array.from(dedos.current.values())[0];
				arrastre.current = q && recorte ? { tipo: "foto", x: q.x, y: q.y, r0: recorte.rect, v: vz } : null;
			}
			return;
		}
		const a = arrastre.current;
		if (!a) return;
		arrastre.current = null; setMoviendo(false);
		if (a.tipo === "mover") encuadrar(true);
		else if (a.tipo !== "foto" && a.tipo !== "debajo") encuadrar();
	};
	// Rueda del mouse y pellizco del trackpad (llega como rueda con Ctrl): acercan donde está el puntero. Escucha nativa
	// para poder frenar el zoom del navegador.
	useEffect(() => {
		const c = capaRef.current;
		if (!c) return undefined;
		const rueda = (e: WheelEvent) => {
			e.preventDefault();
			const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
			zoomRef.current(e.clientX, e.clientY, Math.exp(-dy * (e.ctrlKey ? 0.01 : 0.0018)));
		};
		c.addEventListener("wheel", rueda, { passive: false });
		return () => c.removeEventListener("wheel", rueda);
	}, [recortando, !!k0]);

	useEffect(() => {
		const tecla = (e: KeyboardEvent) => {
			if (recortando) {
				if (e.key === "Escape") salirRecorte();
				if (e.key === "Enter") { e.preventDefault(); aplicarRecorte(); }
				return;
			}
			if (e.key === "Escape") { if (textoEn) setTextoEn(null); else cancelar(); }
			if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !textoEn) { e.preventDefault(); setTrazos((t) => t.slice(0, -1)); }
			if (!textoEn && (e.key === "ArrowLeft" || e.key === "ArrowRight")) ir(idx + (e.key === "ArrowRight" ? 1 : -1));
		};
		window.addEventListener("keydown", tecla);
		return () => window.removeEventListener("keydown", tecla);
	});

	// ---- marcar
	/** De la pantalla a coordenadas de la vista (foto ya recortada y girada). */
	const puntoVista = (e: React.PointerEvent): Punto => {
		const r = lienzo.current!.getBoundingClientRect();
		return { x: limitar(((e.clientX - r.left) / r.width) * vr.w, 0, vr.w), y: limitar(((e.clientY - r.top) / r.height) * vr.h, 0, vr.h) };
	};
	/** De la pantalla a coordenadas de la foto original. */
	const punto = (e: React.PointerEvent): Punto => { const p = puntoVista(e); return aOriginal(p.x + vr.x, p.y + vr.y, vistaAj.giro, W, H); };
	const bajar = (e: React.PointerEvent) => {
		if (!base || guardando || !herr || recortando) return;   // sin herramienta: lo toma el marco para deslizar
		if (e.pointerType === "mouse" && e.button !== 0) return;
		e.stopPropagation();
		if (herr === "texto") {
			// Sin esto el mousedown que sigue le quita el foco al cuadro recién abierto y lo cierra vacío.
			e.preventDefault();
			confirmarTexto(); setTextoEn(puntoVista(e)); setTexto(""); abiertoEn.current = Date.now();
			return;
		}
		if (textoEn) { confirmarTexto(); return; }
		e.preventDefault();
		(e.target as HTMLElement).setPointerCapture?.(e.pointerId);
		pintando.current = true;
		const p = punto(e);
		setEnCurso({ t: herr, color, grosor, pts: [p, p] });
	};
	const mover = (e: React.PointerEvent) => {
		if (!pintando.current) return;
		e.preventDefault();
		const p = punto(e);
		setEnCurso((z) => (z ? { ...z, pts: z.t === "lapiz" ? [...z.pts, p] : [z.pts[0], p] } : z));
	};
	const soltar = () => {
		if (!pintando.current) return;
		pintando.current = false;
		setEnCurso((z) => {
			if (z) {
				const a = z.pts[0]; const b = z.pts[z.pts.length - 1];
				const chico = z.t !== "lapiz" && Math.abs(a.x - b.x) < 3 && Math.abs(a.y - b.y) < 3;
				if (!chico) setTrazos((t) => [...t, z]);
			}
			return null;
		});
	};

	// Deslizar para cambiar de foto (solo sin herramienta, como WhatsApp).
	const deslizBajar = (e: React.PointerEvent) => {
		if (herr || !varias || textoEn || guardando || recortando) return;
		if (e.pointerType === "mouse" && e.button !== 0) return;
		if ((e.target as HTMLElement).closest("button")) return;
		desliz.current = { x: e.clientX, y: e.clientY };
		(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
	};
	const deslizMover = (e: React.PointerEvent) => {
		const d = desliz.current;
		if (!d) return;
		let v = e.clientX - d.x;
		if ((idx === 0 && v > 0) || (idx === fotos.length - 1 && v < 0)) v /= 3;   // en las orillas se resiste
		setDx(v);
	};
	const deslizSoltar = (e: React.PointerEvent) => {
		const d = desliz.current;
		if (!d) return;
		desliz.current = null;
		const ddx = e.clientX - d.x; const ddy = e.clientY - d.y;
		setDx(0);
		if (Math.abs(ddx) > 50 && Math.abs(ddx) > Math.abs(ddy)) ir(idx + (ddx < 0 ? 1 : -1));
	};

	/** Las fotos recortadas o con marcas, ya convertidas en JPEG (null si algo falló). */
	const hornear = async (): Promise<FotoMarcada[] | null> => {
		const todas: Record<string, Trazo[]> = { ...marcas };
		const zt = trazoTexto();
		if (zt) todas[id] = [...(todas[id] || []), zt];
		const salida: FotoMarcada[] = [];
		for (const f of fotos) {
			const z = todas[f.id] || SIN_TRAZOS; const aj = ajustes[f.id] || SIN_AJUSTE; const b = bases.current.get(f.id);
			if ((!z.length && ajusteVacio(aj)) || !b) continue;
			const r = rectDe(aj, b.width, b.height);
			const img = originales.current.get(f.id);
			// Recortada: se saca de la foto original (hasta 2048 px por lado) para que no pierda nitidez.
			const fo = !ajusteVacio(aj) && img ? img.naturalWidth / b.width : 1;
			const esc = Math.max(1, Math.min(fo, LADO_MAX / Math.max(r.w, r.h)));
			const c = document.createElement("canvas");
			c.width = Math.max(1, Math.round(r.w * esc)); c.height = Math.max(1, Math.round(r.h * esc));
			const ctx = c.getContext("2d");
			if (!ctx) return null;
			componer(ctx, b, img, aj, esc, z);
			const blob = await aBlob(c, 0.9);
			if (!blob) return null;
			salida.push({ id: f.id, file: new File([blob], nombreJpg(f.file.name), { type: "image/jpeg", lastModified: Date.now() }) });
		}
		return salida;
	};
	const terminar = async (enviar: boolean) => {
		if (guardando || recortando) return;
		setGuardando(true);
		const hechas = await hornear();
		if (!montado.current) return;
		setGuardando(false);
		if (!hechas) return;
		if (enviar && onEnviar) onEnviar(hechas); else onListo(hechas);
	};

	// Dónde va la cajita de texto en pantalla (coordenadas de la vista → CSS).
	const posTexto = (): React.CSSProperties => {
		const c = lienzo.current; const m = marco.current;
		if (!c || !m || !textoEn) return { display: "none" };
		const r = c.getBoundingClientRect(); const rm = m.getBoundingClientRect();
		const esc = r.width / vr.w;
		return { left: r.left - rm.left + textoEn.x * esc, top: r.top - rm.top + textoEn.y * esc, fontSize: Math.max(14, unidad * 7 * grosor * esc), color };
	};

	const aviso = recortando ? "Arrastra las esquinas o las orillas. Pellizca o usa la rueda del mouse para acercar."
		: herr === "texto" ? "Toca donde quieras escribir; Enter para fijar el texto."
		: herr ? (herr === "lapiz" ? "Dibuja con el dedo o el mouse." : "Arrastra para dibujar.") + " Toca la herramienta otra vez para soltarla."
		: varias ? "Elige una herramienta para marcar o recortar. Desliza para cambiar de foto." : "Elige una herramienta para marcar o recortar.";

	const ratioActual = recorte ? ratioDe(aspecto, RWr, RHr) : null;
	const capa: React.CSSProperties = { left: 0, top: 0, width: area.mw, height: area.mh };
	const cajaRec: React.CSSProperties | null = recorte && K ? { left: ox + recorte.rect.x * K, top: oy + recorte.rect.y * K, width: recorte.rect.w * K, height: recorte.rect.h * K } : null;

	return createPortal(
		<div className={"mf-velo" + (recortando ? " recortando" : "")} role="dialog" aria-modal="true" aria-label="Editar Foto">
			{recortando ? (
				<div className="mf-cab">
					<button type="button" className="btn-wa" onClick={salirRecorte}>Cancelar</button>
					<span className="mf-titulo">Recortar</span>
					<button type="button" className="btn-wa pri" onClick={aplicarRecorte}>Aplicar</button>
				</div>
			) : (
				<div className="mf-cab">
					<button type="button" className="btn-wa" onClick={cancelar} disabled={guardando}>Cancelar</button>
					<span className="mf-titulo">{varias ? "Foto " + (idx + 1) + " de " + fotos.length : "Editar Foto"}</span>
					<button type="button" className="btn-wa pri" onClick={() => terminar(false)} disabled={guardando}>{guardando ? "Guardando…" : "Listo"}</button>
				</div>
			)}
			{recortando ? (
				<div className="mf-barra mf-barra-rec">
					<div className="mf-grupo mf-g-girar">
						<button type="button" className="mf-btn" title="Girar" aria-label="Girar" onClick={girar}>{ICONO_GIRAR}</button>
					</div>
					<div className="mf-grupo mf-g-aspecto" role="group" aria-label="Proporción">
						{ASPECTOS.map((a) => (
							<button key={a.id} type="button" className={"mf-chip" + (aspecto === a.id ? " activo" : "")} aria-pressed={aspecto === a.id} onClick={() => elegirAspecto(a.id)}>{a.nombre}</button>
						))}
					</div>
					<div className="mf-grupo mf-g-restablecer">
						<button type="button" className="mf-chip" onClick={restablecer}>Restablecer</button>
					</div>
				</div>
			) : (
				<div className="mf-barra">
					<div className="mf-grupo mf-g-recortar">
						<button type="button" className="mf-btn" title="Recortar y girar" aria-label="Recortar y girar" disabled={!base || guardando} onClick={entrarRecorte}>{ICONO_RECORTAR}</button>
					</div>
					<div className="mf-grupo mf-g-herr" role="group" aria-label="Herramienta">
						{HERRAMIENTAS.map((h) => (
							<button key={h.id} type="button" className={"mf-btn" + (herr === h.id ? " activo" : "")} title={h.nombre} aria-label={h.nombre} aria-pressed={herr === h.id}
								onClick={() => elegirHerr(h.id)}>{h.icono}</button>
						))}
					</div>
					<div className="mf-grupo mf-g-color" role="group" aria-label="Color">
						{COLORES.map((c) => (
							<button key={c} type="button" className={"mf-color" + (color === c ? " activo" : "")} style={{ background: c }} aria-label={"Color " + c} aria-pressed={color === c}
								onClick={() => { setColor(c); if (!herr) setHerr("lapiz"); }} />
						))}
					</div>
					<div className="mf-grupo mf-g-grosor" role="group" aria-label="Grosor">
						{GROSORES.map((g) => (
							<button key={g.id} type="button" className={"mf-btn" + (grosor === g.id ? " activo" : "")} title={g.nombre} aria-label={g.nombre} aria-pressed={grosor === g.id} onClick={() => setGrosor(g.id)}>
								<span className="mf-punto" style={{ width: 4 + g.id * 6, height: 4 + g.id * 6 }} />
							</button>
						))}
					</div>
					<div className="mf-grupo mf-g-deshacer">
						<button type="button" className="mf-btn" title="Deshacer (Ctrl+Z)" aria-label="Deshacer" disabled={!trazos.length} onClick={() => setTrazos((t) => t.slice(0, -1))}>
							<svg viewBox="0 0 24 24"><path d="M9 14L4 9l5-5M4 9h10a6 6 0 0 1 0 12h-3" /></svg>
						</button>
					</div>
				</div>
			)}
			<div className={"mf-marco" + (!herr && varias && !recortando ? " desliza" : "")} ref={marco}
				onPointerDown={deslizBajar} onPointerMove={deslizMover} onPointerUp={deslizSoltar} onPointerCancel={deslizSoltar}>
				{noAbre ? (
					<div className="mf-aviso">Este navegador no puede abrir esta foto para editarla. Se mandará tal cual.</div>
				) : !base ? (
					<div className="mf-aviso">Cargando foto…</div>
				) : null}
				<canvas ref={lienzo} className={"mf-lienzo " + (recortando ? "nada rec" : herr || "nada") + (dx ? " arrastrando" : "")}
					style={!base || !k ? { display: "none" } : recortando ? { width: area.mw, height: area.mh } : { width: cssW, height: cssH, transform: dx ? "translateX(" + dx + "px)" : undefined }}
					onPointerDown={bajar} onPointerMove={mover} onPointerUp={soltar} onPointerCancel={soltar} onPointerLeave={soltar} />
				{cajaRec ? (
					<div className={"mf-rec-capa" + (vz.z > 1.01 ? " acercada" : "")} style={capa} ref={capaRef}
						onPointerDown={recBajar} onPointerMove={recMover} onPointerUp={recSoltar} onPointerCancel={recSoltar}>
						<div className="mf-rec-sombra" aria-hidden="true"><span style={cajaRec} /></div>
						<div className={"mf-rec-caja" + (moviendo ? " moviendo" : "")} style={cajaRec} data-asa="mover">
							<span className="mf-rec-rejilla" aria-hidden="true"><i /><i /><i /><i /></span>
							{ASAS.filter((a) => !ratioActual || a.length === 2).map((a) => (
								<span key={a} className={"mf-asa mf-asa-" + a} data-asa={a} role="presentation" />
							))}
						</div>
					</div>
				) : null}
				{textoEn && !recortando ? (
					<textarea ref={cajaTexto} className="mf-texto" style={posTexto()} value={texto} rows={1} placeholder="Escribe…" aria-label="Texto" autoFocus
						onChange={(e) => setTexto(e.target.value)}
						onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); confirmarTexto(); } e.stopPropagation(); }}
						onBlur={() => { if (Date.now() - abiertoEn.current < 400) cajaTexto.current?.focus(); else confirmarTexto(); }} />
				) : null}
				{varias && !recortando && idx > 0 ? <button type="button" className="mf-flecha izq" aria-label="Foto anterior" onClick={() => ir(idx - 1)}><svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" /></svg></button> : null}
				{varias && !recortando && idx < fotos.length - 1 ? <button type="button" className="mf-flecha der" aria-label="Foto siguiente" onClick={() => ir(idx + 1)}><svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg></button> : null}
			</div>
			<div className="mf-pie">{aviso}</div>
			{varias && !recortando ? (
				<div className="mf-tira" ref={tira} role="tablist" aria-label="Fotos">
					{fotos.map((f, n) => (
						<button key={f.id} type="button" role="tab" aria-selected={n === idx} aria-label={"Foto " + (n + 1)} className={"mf-mini" + (n === idx ? " activa" : "")} onClick={() => ir(n)}>
							<img src={minis[f.id] || f.url} alt="" draggable={false} />
							{(marcas[f.id] || []).length || !ajusteVacio(ajustes[f.id]) ? <span className="mf-con-marcas" title="Editada"><WappIcono n="edit" /></span> : null}
						</button>
					))}
				</div>
			) : null}
			{onEnviar && !recortando ? (
				<div className="mf-envio">
					{onComentario ? (
						<textarea rows={1} value={comentario || ""} placeholder="Comentario (opcional)" aria-label="Comentario" maxLength={1000} disabled={guardando}
							onChange={(e) => { onComentario(e.target.value); const t = e.target; t.style.height = "44px"; t.style.height = Math.min(110, t.scrollHeight) + "px"; }}
							onKeyDown={(e) => { e.stopPropagation(); if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); terminar(true); } }} />
					) : <span className="mf-esp" />}
					<button type="button" className="mf-enviar" onClick={() => terminar(true)} disabled={guardando} aria-label={textoEnviar || "Enviar"}>
						<svg viewBox="0 0 24 24"><path d="M4 12l16-8-6 16-2.5-6.5z" /></svg><span>{textoEnviar || "Enviar"}</span>
					</button>
				</div>
			) : null}
		</div>,
		document.body,
	);
};

export default MarcarFoto;
